import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon CCMS — Ağustos 2026 Sosyal Medya İçerik & Medya Planı.
 * Temmuz CCMS planıyla (CanonCCMSContentPlanJuly) aynı çatı, aynı içerik mix'i ve
 * aynı bütçe modeli (Efe kararı 2026-08-05: "aynısını ağustosta yapacağız, bütçesi 45K").
 * 2 frame (Future of What's Possible / Future of People), 4 pillar (içerik),
 * FB + IG post bazlı dağılım. Her post 2.500₺. Net medya 32.500₺.
 * Bütçe modeli: brüt 45.000₺ → stopaj + hizmet bedeli kesintisi → 32.500₺ net.
 *
 * Temmuz'dan FARKI: sayfaya Canon'un tam CEA pillar matrisi (3 frame × 18 satır —
 * BU / Unit / Campaign / Sprout label / organik kanal / objective / CTA) referans
 * tablosu olarak eklendi ve boost edilen 4 pillar bu matristeki Sprout etiketleriyle
 * eşleştirildi. Matris müşteriden geldi (CEA sheet), plan satırları onun alt kümesi.
 */
export default function CanonCCMSContentPlanAugust() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [showMatrix, setShowMatrix] = useState(true);

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');

  // ── Bütçe modeli ───────────────────────────────────────────────────────────
  const PER_POST = 2500;             // post başına medya bütçesi
  const GROSS = 45000;               // müşteri brüt bütçesi
  const NET = 32500;                 // stopaj + hizmet bedeli sonrası net medya
  const DEDUCT = GROSS - NET;        // 12.500 — stopaj + hizmet bedeli vb.
  const LIVE_DAYS = 5;               // CCMS kampanyaları 5 gün yayında

  // ── İçerik / pillar dağılımı (Temmuz ile birebir aynı) ─────────────────────
  // sprout: müşterinin CEA matrisindeki "Sprout Label Name CEA" karşılığı.
  const pillarDefs = [
    { frame: "Future of What's Possible", name: 'Tuesday Tip – Haftanın Canon İpucu', fb: 1, ig: 1, objective: 'Traffic', sprout: 'CORP_Tuesday Tip', highlight: true },
    { frame: "Future of What's Possible", name: 'Technology Highlight', fb: 1, ig: 1, objective: 'Traffic', sprout: 'CORP_Technology Highlight' },
    { frame: 'Future of People', name: 'UGC – Theme Announcement', fb: 1, ig: 1, objective: 'Engagement', sprout: 'CORP_UGC', highlight: true },
    { frame: 'Future of People', name: 'UGC – Reshare', fb: 2, ig: 5, objective: 'Engagement', sprout: 'CORP_UGC' },
  ];
  const pillars = pillarDefs.map((p) => {
    const posts = p.fb + p.ig;
    return { ...p, posts, budget: posts * PER_POST };
  });

  const totals = pillars.reduce((t, p) => ({
    fb: t.fb + p.fb, ig: t.ig + p.ig, posts: t.posts + p.posts, budget: t.budget + p.budget,
  }), { fb: 0, ig: 0, posts: 0, budget: 0 });

  const byObjective = pillars.reduce((m, p) => {
    m[p.objective] = (m[p.objective] || 0) + p.budget;
    return m;
  }, {});
  const trafficBudget = byObjective.Traffic || 0;       // 10.000
  const engagementBudget = byObjective.Engagement || 0; // 22.500

  // ── Tahmini performans (varsayımlı — Temmuz planıyla aynı birimler) ────────
  const CPM = 28;     // blended gösterim maliyeti
  const FREQ = 2.4;   // ortalama frekans
  const CPC = 2.80;   // trafik tıklama maliyeti
  const CPE = 0.55;   // etkileşim başına maliyet
  const impressions = Math.round((NET / CPM) * 1000);
  const reach = Math.round(impressions / FREQ);
  const clicks = Math.round(trafficBudget / CPC);
  const engagements = Math.round(engagementBudget / CPE);

  // ── Canon CEA pillar matrisi (müşteri kaynağı, birebir) ────────────────────
  // Boost planına giren satırlar `boosted: true` ile işaretli.
  const CEA_MATRIX = [
    {
      bu: 'FUTURE OF PEOPLE',
      rows: [
        { unit: 'Corporate', campaign: 'UGC', pillar: '#CanonTürkiye (8 post in a month)', sproutFrame: 'Future of People', sprout: 'CORP_UGC', organic: 'FB - IG - IGS', objective: 'Engagement', paid: 'Facebook | Instagram', cta: 'no page', boosted: true },
        { unit: 'Corporate', campaign: 'Local Corporate Campaigns', pillar: 'Inside Canon (1-2 posts in a month - adhoc)', sproutFrame: 'Future of People', sprout: 'CORP_HR* / CORP_Always On', sproutNote: '*Bu eklenince CORP_Future of People eklenemez', organic: 'LK – FB', objective: 'Engagement', paid: 'Facebook | Instagram', cta: 'no page' },
        { unit: 'Corporate', campaign: 'Local Corporate Campaign', pillar: 'Celebration of International Key Dates (1-2 posts in a month)', sproutFrame: 'Future of People', sprout: 'CORP_Always On', organic: 'LK – IG/IGS - FB', objective: 'Engagement', paid: 'Facebook | Instagram', cta: 'no page' },
        { unit: 'Corporate', campaign: 'Local Corporate Campaigns', pillar: 'Corporate View (2 in a month)', sproutFrame: 'Future of People', sprout: '—', organic: 'LK – IG/IGS - FB', objective: 'Traffic', paid: 'Facebook', cta: '/view/' },
        { unit: 'Corporate', campaign: 'Central / Local Corporate Campaigns', pillar: 'Brand Campaign / World Unseen', sproutFrame: 'Future of Planet', sprout: 'CORP_World Unseen', organic: 'LK – IG/IGS - FB', objective: 'Engagement / Traffic', paid: 'Facebook | Instagram', cta: '/view/unseen' },
        { unit: 'Corporate', campaign: 'Central Corporate Campaigns', pillar: 'Young People Programme (2 in a month)', sproutFrame: 'Future of People', sprout: '—', organic: 'LK – IG/IGS - FB', objective: 'Engagement', paid: 'Facebook | Instagram', cta: 'sustainability/empowering-young-people/' },
      ],
    },
    {
      bu: "FUTURE OF WHAT'S POSSIBLE",
      rows: [
        { unit: 'Consumer/Business', campaign: 'Local Business Unit Campaigns', pillar: 'BU Traffic (Events / Campaigns / Collaborations)', sproutFrame: "Future of What's Possible", sprout: 'B2C_Events', sproutNote: '*B2B event etiketi yok — yürüyen kampanyaya göre; uyarsa DP&S Production Print', organic: 'LK - IG - IGS - FB', objective: 'Traffic', paid: 'Facebook', cta: 'post bazında değişir' },
        { unit: 'Consumer/Business', campaign: 'Local Business Unit Campaigns', pillar: 'BU Awareness (Events / Campaigns / Collaborations)', sproutFrame: "Ürünse Future of What's Possible, çalışanlarsa Future of People", sprout: 'Yürüyen kampanyaya göre', organic: 'LK - IG - IGS - FB', objective: 'Engagement', paid: 'Facebook - Instagram', cta: 'no page' },
        { unit: 'Consumer/Business', campaign: 'Product Focus (B2C & B2B)', pillar: 'Technology Highlights (2 posts in a month, one for B2B & B2C)', sproutFrame: "Future of What's Possible", sprout: 'CORP_Technology Highlight', sproutNote: '*B2B özel etiketi yok', organic: 'LK - IG - IGS - FB', objective: 'Traffic', paid: 'Facebook', cta: 'post bazında değişir', boosted: true },
        { unit: 'Corporate', campaign: 'Local Corporate Campaign', pillar: 'Celebration of International Key Dates / World Photography Day (1-2 posts in a month)', sproutFrame: 'Future of People', sprout: 'CORP_Inside Canon', sproutNote: '*Geçmişte sadece UGC etiketi kullanıldı', organic: 'LK – IG/IGS - FB', objective: 'Engagement', paid: 'Facebook | Instagram', cta: 'no page' },
        { unit: 'Corporate', campaign: 'Local Corporate Campaigns', pillar: 'Corporate View (2 in a month)', sproutFrame: 'Future of People', sprout: '—', organic: 'LK – IG/IGS - FB', objective: 'Traffic', paid: 'Facebook', cta: '/view/' },
        { unit: 'Consumer/Business', campaign: 'Product Focus (B2C & B2B)', pillar: 'Tuesday Tip (2 posts in a month, one for B2C)', sproutFrame: "Future of What's Possible", sprout: 'CORP_Tuesday Tip', organic: 'LK – IG/IGS - FB', objective: 'Traffic', paid: 'Facebook', cta: 'post bazında değişir', boosted: true },
        { unit: 'Corporate', campaign: 'Local Corporate Campaigns', pillar: 'See No Limits', sproutFrame: "Future of What's Possible", sprout: 'ITCG_See No Limits', organic: 'LK – IG/IGS - FB', objective: 'Engagement', paid: 'Facebook - Instagram', cta: 'See No Limits sayfası var (CEA: TBC)' },
      ],
    },
    {
      bu: 'FUTURE OF PLANET',
      rows: [
        { unit: 'Corporate', campaign: 'CEL Corporate Campaign', pillar: 'Corporate Sustainability (1-2 posts per month)', sproutFrame: 'Future of Planet', sprout: 'CORP_Susty', organic: 'LK – IG/IGS - FB', objective: 'Traffic', paid: 'Facebook', cta: '/sustainability/' },
        { unit: 'Corporate', campaign: 'Local Corporate Campaign', pillar: 'Celebration of International Key Dates — ör. World Water Day', sproutFrame: 'Future of People', sprout: 'CORP_Always On', organic: 'LK – IG/IGS - FB', objective: 'Engagement', paid: 'Facebook | Instagram', cta: 'no page' },
        { unit: 'Corporate', campaign: 'Local Corporate Campaigns', pillar: 'Corporate View (2 in a month)', sproutFrame: 'Future of People', sprout: '—', organic: 'LK – IG/IGS - FB', objective: 'Traffic', paid: 'Facebook', cta: '/view/' },
        { unit: 'Corporate', campaign: 'Central / Local Corporate Campaigns', pillar: 'Brand Campaign / Coral Campaign (2 posts per month)', sproutFrame: "Ürünse Future of What's Possible, çalışanlarsa Future of People", sprout: 'Corp_World Unseen_Coral', organic: 'LK – IG/IGS - FB', objective: 'Traffic', paid: 'Facebook | Instagram', cta: '/view/unseen' },
        { unit: 'Corporate', campaign: 'Local Corporate Campaigns', pillar: '—', sproutFrame: "Future of What's Possible", sprout: '—', organic: 'LK – IG/IGS - FB', objective: 'Engagement', paid: 'Facebook', cta: 'no page' },
      ],
    },
  ];
  const matrixRowCount = CEA_MATRIX.reduce((n, g) => n + g.rows.length, 0);

  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(slideRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m, rem = h;
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= (ph - m * 2);
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= (ph - m * 2);
      }
      pdf.save('Canon_CCMS_Icerik_Plani_Agustos_2026.pdf');
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  const ObjBadge = ({ objective }) => (
    <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
      objective === 'Traffic' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-violet-50 text-violet-700 border-violet-200'
    }`}>{objective}</span>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end gap-2 mb-3">
        <button
          onClick={() => setShowMatrix((v) => !v)}
          className="text-gray-700 bg-white border border-gray-300 text-sm font-semibold px-4 py-2 rounded-lg shadow-sm"
        >
          {showMatrix ? 'CEA Matrisini Gizle' : 'CEA Matrisini Göster'}
        </button>
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: '#CC0000' }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">CCMS — Sosyal Medya İçerik &amp; Medya Planı · Ağustos 2026</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Traffic &amp; Engagement · CCMS kampanyaları 5 gün yayında</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Plan özeti */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Plan Özeti — Ağustos 2026</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon CCMS sosyal medya içerik planı: <strong>2 frame</strong> (Future of What's Possible / Future of People)
              altında <strong>4 içerik (pillar)</strong>, Facebook + Instagram'da toplam <strong>{totals.posts} gönderi</strong>.
              Her gönderiye <strong>{fmtCur(PER_POST)}</strong> medya bütçesi ayrılır; CCMS kampanyaları <strong>{LIVE_DAYS} gün</strong> yayında kalır.
              Toplam net medya harcaması <strong>{fmtCur(NET)}</strong>. İçerik mix'i ve bütçe modeli <strong>Temmuz 2026 planıyla birebir aynıdır</strong>.
            </p>
          </div>

          {/* Bütçe kırılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Bütçe Kırılımı</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Toplam Bütçe (Brüt)" value={fmtCur(GROSS)} sub="müşteri toplam" />
              <Stat label="Stopaj + Hizmet Bedeli" value={'-' + fmtCur(DEDUCT)} sub="kesintiler" tone="indigo" />
              <Stat label="NET Medya" value={fmtCur(NET)} sub="Meta'da harcanan" tone="green" />
              <Stat label="Post Başına" value={fmtCur(PER_POST)} sub={`${totals.posts} gönderi`} />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Brüt {fmtCur(GROSS)} bütçeden stopaj + hizmet bedeli ({fmtCur(DEDUCT)}) düşüldükten sonra
              reklamda gerçek harcanan net tutar {fmtCur(NET)}'dir. Hedef bazlı: Traffic {fmtCur(trafficBudget)} · Engagement {fmtCur(engagementBudget)}.
            </p>
          </div>

          {/* İçerik / pillar tablosu */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">İçerik &amp; Bütçe Dağılımı ({pillars.length} Pillar)</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Frame</th>
                    <th className="text-left px-3 py-2 font-semibold">Pillar (İçerik)</th>
                    <th className="text-left px-3 py-2 font-semibold">Sprout Label</th>
                    <th className="text-right px-3 py-2 font-semibold">FB Post</th>
                    <th className="text-right px-3 py-2 font-semibold">IG Post</th>
                    <th className="text-center px-3 py-2 font-semibold">Hedef</th>
                    <th className="text-right px-3 py-2 font-semibold">Bütçe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pillars.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{p.frame}</td>
                      <td className={`px-3 py-2 font-semibold ${p.highlight ? 'text-red-700' : 'text-gray-800'}`}>{p.name}</td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-[10px]">{p.sprout}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.fb} × {fmtInt(PER_POST)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.ig} × {fmtInt(PER_POST)}</td>
                      <td className="px-3 py-2 text-center"><ObjBadge objective={p.objective} /></td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtCur(p.budget)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totals.fb} post</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totals.ig} post</td>
                    <td className="px-3 py-2 text-center">{totals.posts} gönderi</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Tahmini performans */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Tahmini Performans</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Toplam Gösterim" value={fmtInt(impressions)} sub={`CPM ≈ ₺${CPM}`} />
              <Stat label="Tahmini Erişim" value={fmtInt(reach)} sub={`~${FREQ} frekans`} tone="green" />
              <Stat label="Trafik Tıklaması" value={fmtInt(clicks)} sub={`${fmtCur(trafficBudget)} · CPC ≈ ₺${CPC.toFixed(2)}`} tone="red" />
              <Stat label="Etkileşim" value={fmtInt(engagements)} sub={`${fmtCur(engagementBudget)} · CPE ≈ ₺${CPE.toFixed(2)}`} tone="indigo" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              *Tahminler varsayımlıdır (CPM ₺{CPM}, frekans ~{FREQ}, CPC ₺{CPC.toFixed(2)}, CPE ₺{CPE.toFixed(2)}).
              Sayılar Meta açık artırma dinamiklerine göre değişir; optimizasyonla yön verilir.
            </p>
          </div>

          {/* CEA pillar matrisi (referans) */}
          {showMatrix && (
            <div>
              <p className="text-sm font-bold text-gray-800 mb-1">CEA Pillar Matrisi — Referans ({matrixRowCount} satır)</p>
              <p className="text-[10px] text-gray-500 mb-2">
                Canon'un tam CEA içerik taksonomisi. Yukarıdaki medya planı bu matrisin
                <span className="mx-1 inline-block px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 font-semibold">boost edilen</span>
                alt kümesidir; kalan satırlar organik akışta yayınlanır.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 uppercase text-[9px]">
                      <th className="text-left px-2 py-2 font-semibold">Unit</th>
                      <th className="text-left px-2 py-2 font-semibold">Campaign</th>
                      <th className="text-left px-2 py-2 font-semibold">CEA Pillar (İçerik)</th>
                      <th className="text-left px-2 py-2 font-semibold">Sprout Frame</th>
                      <th className="text-left px-2 py-2 font-semibold">Sprout Label</th>
                      <th className="text-left px-2 py-2 font-semibold">Organik Kanal</th>
                      <th className="text-center px-2 py-2 font-semibold">Objective</th>
                      <th className="text-left px-2 py-2 font-semibold">Paid Kanal</th>
                      <th className="text-left px-2 py-2 font-semibold">CTA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {CEA_MATRIX.map((g) => (
                      <React.Fragment key={g.bu}>
                        <tr>
                          <td colSpan={9} className="px-2 py-1.5 bg-gray-100 font-bold text-gray-700 tracking-wide text-[10px]">{g.bu}</td>
                        </tr>
                        {g.rows.map((r, i) => (
                          <tr key={`${g.bu}-${i}`} className={r.boosted ? 'bg-red-50/60' : 'hover:bg-gray-50'}>
                            <td className="px-2 py-1.5 text-gray-500 align-top">{r.unit}</td>
                            <td className="px-2 py-1.5 text-gray-500 align-top">{r.campaign}</td>
                            <td className={`px-2 py-1.5 align-top ${r.boosted ? 'font-semibold text-red-700' : 'text-gray-800'}`}>
                              {r.pillar}
                              {r.boosted && <span className="ml-1 text-[8px] uppercase font-bold text-red-600">· boost</span>}
                            </td>
                            <td className="px-2 py-1.5 text-gray-500 align-top">{r.sproutFrame}</td>
                            <td className="px-2 py-1.5 text-gray-600 font-mono align-top">
                              {r.sprout}
                              {r.sproutNote && <span className="block text-[8px] text-gray-400 font-sans mt-0.5">{r.sproutNote}</span>}
                            </td>
                            <td className="px-2 py-1.5 text-gray-500 align-top">{r.organic}</td>
                            <td className="px-2 py-1.5 text-center align-top">
                              <ObjBadge objective={r.objective === 'Traffic' ? 'Traffic' : 'Engagement'} />
                              {r.objective === 'Engagement / Traffic' && <span className="block text-[8px] text-gray-400 mt-0.5">+ Traffic</span>}
                            </td>
                            <td className="px-2 py-1.5 text-gray-500 align-top">{r.paid}</td>
                            <td className="px-2 py-1.5 text-gray-500 align-top break-all">{r.cta}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[9px] text-gray-400 mt-2 leading-relaxed">
                Not: Matriste UGC organik olarak <strong>ayda 8 post</strong> öngörülüyor; medya planındaki 9 UGC satırı
                gönderi değil <strong>reklam</strong> sayısıdır — aynı içeriğin Facebook ve Instagram kopyaları Meta'da ayrı reklam olarak sayılır.
                "Corporate View" matriste üç BU altında da tekrar ediyor (kaynak dosyadaki hâliyle bırakıldı).
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">
              Ağustos 2026 · Confidential · Jellybon × Glowytics
            </p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
